/**
 * G36 - every country as a table (country, PlayStation region, players, share),
 * the Multiplayer counterpart of the Sony/DOMO "PS5 Players by Country" card.
 * G27 charts the top 15; this one lists them all.
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ChartCard, EmptyChart, useChartTheme } from './MpCharts';
import { REGION_INFO, countryName, regionOf, type Region } from '@/utils/countryRegions';

const REGION_PILL: Record<Region, string> = {
  SIEE: 'bg-green-500/20 text-green-400',
  SIEA: 'bg-blue-500/20 text-blue-400',
  SIEAsia: 'bg-orange-500/20 text-orange-400',
  SIEJ: 'bg-red-500/20 text-red-400',
  'Non-SIE': 'bg-gray-500/20 text-gray-400',
};
const COLLAPSED_ROWS = 10;

export interface CountryPlayers {
  country: string;
  opened: number;
  played: number;
}

export function PlayersByCountryCard({ rows }: { rows: CountryPlayers[] }) {
  const { isDark } = useChartTheme();
  const [showAll, setShowAll] = useState(false);
  const total = rows.reduce((s, r) => s + r.opened, 0);
  const shown = showAll ? rows : rows.slice(0, COLLAPSED_ROWS);

  return (
    <ChartCard id="G36" title="Players by Country" badges={['lowerBound', 'noRegion']} n={total} height={320}
      right={rows.length > COLLAPSED_ROWS && (
        <button onClick={() => setShowAll(!showAll)} className={`flex items-center gap-1 text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
          {showAll ? 'Show Less' : `Show All (${rows.length})`}
          {showAll ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      )}>
      {rows.length === 0 ? <EmptyChart /> : (
        <div className="h-full overflow-auto">
          <table className="w-full text-sm">
            <thead><tr className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <th className="text-left py-2">Country</th><th className="text-left py-2">Region</th>
              <th className="text-right py-2">Players</th><th className="text-right py-2">%</th>
              <th className="text-right py-2 pr-3" title="Players with at least one match report">Played</th>
            </tr></thead>
            <tbody>
              {shown.map(r => {
                const region = regionOf(r.country);
                return (
                  <tr key={r.country} className={`border-t ${isDark ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-700'}`}>
                    <td className="py-2">{countryName(r.country)}</td>
                    <td className="py-2">
                      <span title={REGION_INFO[region]} className={`px-2 py-0.5 rounded text-xs ${REGION_PILL[region]}`}>{region}</span>
                    </td>
                    <td className="text-right py-2">{r.opened.toLocaleString()}</td>
                    <td className="text-right py-2">{total ? (r.opened / total * 100).toFixed(1) : '0.0'}%</td>
                    <td className="text-right py-2 pr-3">{r.played.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
}
